const MONTH_YEAR_PATTERN = /^(0[1-9]|1[0-2])\/\d{4}$/;

export function isValidMonthYearDate(value) {
  const trimmed = String(value ?? "").trim();
  return MONTH_YEAR_PATTERN.test(trimmed);
}

/**
 * Format API / stored dates as MM/YYYY for forms and display.
 */
export function formatCertDate(dateStr) {
  if (dateStr == null || dateStr === "") return "";
  const trimmed = String(dateStr).trim();
  if (trimmed.toLowerCase() === "null") return "";
  if (/^\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const date = new Date(trimmed);
  if (!isNaN(date.getTime())) {
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${month}/${year}`;
  }
  return trimmed;
}

/**
 * True when the certification record has a non-empty expiry date value.
 */
export function hasCertificationExpiryDate(cert) {
  const raw = cert?.expiryDate ?? cert?.expiry_date;
  if (raw == null) return false;
  const trimmed = String(raw).trim();
  return trimmed !== "" && trimmed.toLowerCase() !== "null";
}

/**
 * Derive noExpiry: 1 = no expiry, 0 = has expiry.
 * When expiry date is missing, treat as no expiry even if no_expiry was 0 from the API.
 */
export function resolveCertificationNoExpiry(cert) {
  return hasCertificationExpiryDate(cert) ? 0 : 1;
}

/**
 * Map API / stored certification fields for dialog display only (does not alter save payload).
 */
export function certificationToFormValues(cert) {
  if (!cert || typeof cert !== "object") return cert;

  const noExpiry = resolveCertificationNoExpiry(cert) === 1;

  return {
    id: cert.id,
    certificationName:
      cert.certificationName || cert.certification_name || "",
    issuingOrganization:
      cert.issuingOrganization || cert.issuing_organization || "",
    issueDate: formatCertDate(cert.issueDate || cert.issue_date),
    expiryDate: noExpiry
      ? ""
      : formatCertDate(cert.expiryDate || cert.expiry_date),
    noExpiry,
    credentialId: cert.credentialId || cert.credential_id || "",
    credentialUrl: cert.credentialUrl || cert.credential_url || "",
    declaration:
      cert.declaration === true ||
      cert.terms_accepted === 1 ||
      cert.terms_accepted === true ||
      cert.terms_accepted === "1",
  };
}

/**
 * Human-readable issue / expiry line for certification cards (display only).
 */
export function formatCertificationDateRange(cert) {
  const noExpiry = resolveCertificationNoExpiry(cert) === 1;
  const issueDate = formatCertDate(cert?.issueDate || cert?.issue_date);
  if (noExpiry) {
    return issueDate ? `${issueDate} — No Expiry` : "No Expiry";
  }
  const expiryDate = formatCertDate(cert?.expiryDate || cert?.expiry_date);
  if (issueDate && expiryDate) return `${issueDate} — ${expiryDate}`;
  if (issueDate) return issueDate;
  if (expiryDate) return expiryDate;
  return "—";
}
